import { CaretSortIcon, CheckIcon } from '@radix-ui/react-icons'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'

import { cn } from '#lib/utils'

/* this file was generated by IA to prevent weirdo focus */

export interface BookingChapter {
  id: number
  name: string
  imageUrl: string | null
  grade: string
  subject: string
  duration?: string
  modules: {
    id: number
    name: string
    chapterNumber: string
  }[]
}

interface SelectChapterProps {
  chapter: BookingChapter
  value?: string
  onValueChange: (value: string) => void
  disabled?: boolean
}

export const SelectChapter: React.FC<SelectChapterProps> = ({
  chapter,
  value,
  onValueChange,
  disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Trouver le nom du module sélectionné pour l'afficher dans le bouton
  const selectedModule = chapter.modules.find((m) => m.id.toString() === value)

  // Fermer le menu si on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleSelect = (id: string) => {
    onValueChange(id)
    setIsOpen(false)
  }

  return (
    <div className="relative w-full" ref={containerRef}>
      {/* TRIGGER (Le bouton principal) */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setIsOpen(!isOpen)
        }}
        className={cn(
          'shadow-custom flex h-9 w-full items-center justify-between whitespace-nowrap rounded-full border border-neutral-200 bg-transparent px-3 py-2 text-sm placeholder:text-neutral-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:border-neutral-800 dark:placeholder:text-neutral-400',
          isOpen && 'ring-1 ring-neutral-950 dark:ring-neutral-300'
        )}
      >
        <span className="truncate">
          {selectedModule ? selectedModule.name : 'Choisir un module'}
        </span>
        <CaretSortIcon className="ml-2 size-4 opacity-50" />
      </button>

      {/* DROPDOWN (La liste des modules) */}
      {isOpen && (
        <div
          className={cn(
            'animate-in fade-in zoom-in-95 absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-neutral-200 bg-white p-1 text-neutral-950 shadow-md duration-100 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-50'
          )}
        >
          {chapter.modules.map((m) => {
            const isSelected = m.id.toString() === value
            return (
              <div
                key={m.id}
                onClick={() => {
                  handleSelect(m.id.toString())
                }}
                className={cn(
                  'relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none hover:bg-neutral-100 dark:hover:bg-neutral-800',
                  isSelected && 'bg-neutral-50 dark:bg-neutral-900'
                )}
              >
                <span className="truncate">{m.name}</span>
                {isSelected && (
                  <span className="absolute right-2 flex size-3.5 items-center justify-center">
                    <CheckIcon className="size-4" />
                  </span>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
